// src/components/resources/tools/generators/shared/ExportButton.tsx

import { ArrowDownTrayIcon } from '@heroicons/react/24/solid'
import type { ReactElement, ReactNode } from 'react'

import { cn } from '~/lib/utils/design'

export interface ExportButtonProps {
  /**
   * Whether *this* button's export is in progress — drives `aria-busy` and the
   * label/spinner swap (and `disabled` unless `disabled` is given explicitly).
   */
  isExporting: boolean
  /**
   * Force the disabled state independently of `isExporting`. Dual-format tools
   * (PNG + WebP) pass `disabled={anyExportInFlight}` so clicking the *other*
   * format while one is capturing is blocked in the UI too — the spinner still
   * shows only on the button whose `isExporting` is true. Defaults to `isExporting`.
   */
  disabled?: boolean
  /** Kick off the export. */
  onExport: () => void
  /** Idle label. Defaults to `'Download PNG'`. */
  label?: ReactNode
  /** Label shown while exporting. Defaults to `'Exporting…'`. */
  exportingLabel?: ReactNode
  /** Idle leading icon. Defaults to a download arrow. */
  icon?: ReactNode
  /** Per-tool classes always applied (brand color, size, shape). */
  className?: string
  /** Extra classes applied only when idle (e.g. hover styles). */
  idleClassName?: string
  /** Extra classes applied only while exporting (e.g. dimmed). */
  exportingClassName?: string
  /** Accessible name when the visible label isn't sufficient. */
  'aria-label'?: string
  /** Optional id for label/description association. */
  id?: string
}

const SPINNER = (
  <span
    aria-hidden="true"
    className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
  />
)

/**
 * The single accessible export affordance shared by every generator's Download
 * button. It standardizes the *loading contract* — sets **both** `disabled` and
 * `aria-busy` while exporting and swaps a spinner + "Exporting…" for the idle
 * icon + label — so a tool can't ship a half-accessible button. Brand color,
 * size and shape stay per-tool via the `*ClassName` props, so adopting it never
 * changes a tool's visual identity, only makes the busy state consistent.
 *
 * The base layout (`inline-flex items-center justify-center gap-2`) matches what
 * most generators already used; classes are merged with `cn` (tailwind-merge) so
 * a caller's `className` reliably overrides a conflicting base utility (e.g. a
 * tool that wants `gap-1`).
 */
export function ExportButton({
  isExporting,
  disabled,
  onExport,
  label = 'Download PNG',
  exportingLabel = 'Exporting…',
  icon = <ArrowDownTrayIcon className="h-4 w-4" />,
  className,
  idleClassName,
  exportingClassName,
  ...rest
}: ExportButtonProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onExport}
      disabled={disabled ?? isExporting}
      aria-busy={isExporting}
      className={cn(
        'inline-flex items-center justify-center gap-2',
        className,
        isExporting ? exportingClassName : idleClassName,
      )}
      {...rest}
    >
      {isExporting ? (
        <>
          {SPINNER}
          {exportingLabel}
        </>
      ) : (
        <>
          {icon}
          {label}
        </>
      )}
    </button>
  )
}
